import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../../app.config';
import { UtilHelper } from '../../../shared/helpers/utils.helper';
import { RequestService } from '../../../shared/services/request.service';
import { BusinessHour } from '../../store/models/business-hour.model';
import { ChargeOption } from './enum/charge-option.enum';
import { ProductRegisterType } from './enum/product-register-type.enum';
import { ComplementOption } from './models/complement-options.model';
import { Complement } from './models/complement.model';
import { Product } from './models/product.model';

@Injectable()
export class ProductService {
  constructor(private requestService: RequestService) {}

  /**
   * PRODUCTS
   * =====================================
   */

  async getAll(category: number): Promise<Array<Product>> {
    const url: string = APP_CONFIG.WEBSERVICE.GET_PRODUCTS + '?categoryId=' + category;
    const urlRequest: string = this.requestService.buildUrlPathParams({ category: category }, url);

    const response: Array<Product> = await this.requestService.getData(urlRequest);
    let products: Array<Product> = new Array();
    if (response && response.length > 0) {
      response.forEach((p: any) => {
        products.push(new Product(p));
      });
    }
    products = UtilHelper.orderListByStatusAndOrder(products);
    console.log(products);
    return products;
  }

  /**
   * 14/12/2023
   * Métodos insertOrUpdateFromForm e updateOtherFields usam o mesmo serviço.
   * Foi separado devido a confusão nos campos de preço que ao salvar um produto fora da tela de cadastro, está mudando o preço do produto.
   * Ver comentário inserido no nome dos campos no model Product.
   * @param product
   * @returns
   */
  async insertOrUpdateFromForm(product: Product): Promise<any> {
    const data: any = JSON.parse(JSON.stringify(product.toJson()));
    const response: any = await this.requestService.postData(
      APP_CONFIG.WEBSERVICE.PRODUCTS,
      data,
      true
    );
    return UtilHelper.getIdFromLocationPath(response.location);
  }

  /**
   * 14/12/2023
   * Métodos insertOrUpdateFromForm e updateOtherFields usam o mesmo serviço.
   * Foi separado devido a confusão nos campos de preço que ao salvar um produto fora da tela de cadastro, está mudando o preço do produto.
   * Ver comentário inserido no nome dos campos.
   * @param product
   * @returns
   */
  async updateOtherFields(product: Product): Promise<any> {
    /**
     * Quando se tem apenas atualização de campos fora da tela de cadastro, é preciso setar no campo price o valor do mainPrice
     * para evitar alterar o valor dos produtos. Ver comentário inserido no nome dos campos no model Product.
     * Isso que é feito ao selecionar um produto da lista para ir para a tela de cadastro do produto.
     */
    product.price = product.mainPrice;
    const data: any = JSON.parse(JSON.stringify(product.toJson()));
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.PRODUCTS, data);
  }

  async changeAllProductAvailability(item: any): Promise<any> {
    const data: any = {
      categoryId: item.categoryId,
      localOrder: item.localOrder,
      delivery: item.delivery,
      takeaway: item.takeaway,
    };
    return await this.requestService.putData(
      APP_CONFIG.WEBSERVICE.CHANGE_ALL_PRODUCTS_MENU_AVAILABILITY,
      data
    );
  }

  async changeProductAvailability(product: Product): Promise<any> {
    const data: any = {
      productId: product.id,
      localOrder: product.localOrder,
      delivery: product.delivery,
      takeaway: product.takeaway,
    };
    return await this.requestService.putData(APP_CONFIG.WEBSERVICE.CHANGE_MENU_AVAILABILITY, data);
  }

  async duplicate(productId: number): Promise<any> {
    return await this.requestService.postData(
      APP_CONFIG.WEBSERVICE.PRODUCTS_DUPLICATE + '/' + productId
    );
  }

  async remove(productId: number): Promise<any> {
    const url: string = APP_CONFIG.WEBSERVICE.PRODUCTS + '/' + productId;
    return await this.requestService.deleteData(url);
  }

  async updateProductsOrder(products: Array<Product>): Promise<any> {
    const productsJson: Array<any> = new Array();
    if (products && products.length > 0) {
      products.forEach((p: Product, index) => {
        p.order = index + 1;
        productsJson.push(p.toReorderJson());
      });
    }
    return await this.requestService.putData(APP_CONFIG.WEBSERVICE.PRODUCTS_REORDER, productsJson);
  }

  getListProductType(): Array<ProductRegisterType> {
    return [
      ProductRegisterType.APPETIZER,
      ProductRegisterType.MAIN_COURSE,
      ProductRegisterType.DRINKS,
      ProductRegisterType.DESSERTS,
      ProductRegisterType.OTHERS,
    ];
  }

  getListPriceType(): Array<ChargeOption> {
    return [
      ChargeOption.SUM_TOTAL,
      ChargeOption.HIGHEST_VALUE,
      ChargeOption.LOWER_VALUE,
      ChargeOption.AVERAGE,
    ];
  }

  /**
   * COMPLEMENTS
   * =====================================
   */

  async loadComplements(productId: number): Promise<Array<Complement>> {
    const url: string = APP_CONFIG.WEBSERVICE.GET_COMPLEMENTS + '?productId=' + productId;
    const response = (await this.requestService.getData(url)) || [];
    const complements: Array<Complement> = [];
    if (response && response.length > 0) {
      response.forEach((c: any) => {
        complements.push(new Complement(c));
      });
    }
    return complements;
  }

  async saveComplement(complement: Complement): Promise<any> {
    console.log(JSON.stringify(complement.toJson()));
    if (complement.id == null) {
      const response: any = await this.requestService.postData(
        APP_CONFIG.WEBSERVICE.COMPLEMENTS,
        complement.toJson(),
        true
      );
      return UtilHelper.getIdFromLocationPath(response.location);
    } else {
      return await this.requestService.putData(
        APP_CONFIG.WEBSERVICE.COMPLEMENTS,
        complement.toJson()
      );
    }
  }

  async duplicateComplement(complement: Complement): Promise<void> {
    const data: any = {
      complementId: complement.id,
      productId: complement.productId,
    };
    return await this.requestService.postData(APP_CONFIG.WEBSERVICE.COMPLEMENTS_DUPLICATE, data);
  }

  async deleteComplement(complementId: number): Promise<void> {
    return await this.requestService.deleteData(
      APP_CONFIG.WEBSERVICE.COMPLEMENTS + '/' + complementId
    );
  }

  async updateComplementsOrder(complements: Array<Complement>): Promise<any> {
    const complementsJson: Array<any> = [];
    if (complements && complements.length > 0) {
      complements.forEach((c: Complement, index) => {
        c.ordination = index + 1;
        complementsJson.push(c.toReorderJson());
      });
    }
    return await this.requestService.putData(
      APP_CONFIG.WEBSERVICE.COMPLEMENTS_REORDER,
      complementsJson
    );
  }

  /**
   * COMPLEMENTS OPTIONS
   * =====================================
   */

  async getComplementOptions(complementId: number): Promise<Array<ComplementOption>> {
    const url: string = APP_CONFIG.WEBSERVICE.COMPLEMENT_OPTIONS + '?complementId=' + complementId;
    const response: any = await this.requestService.getData(url);
    const complementOptions: Array<ComplementOption> = new Array<ComplementOption>();
    if (response && response.length > 0) {
      response.forEach((co: any) => {
        complementOptions.push(new ComplementOption(co));
      });
    }
    complementOptions.sort((c1, c2) => {
      return c1.status['code'] > c2.status['code'] ? 1 : -1;
    });
    return complementOptions;
  }

  async saveComplementOption(complementOption: ComplementOption): Promise<any> {
    if (complementOption.id == null) {
      //INSERT
      const response: any = await this.requestService.postData(
        APP_CONFIG.WEBSERVICE.COMPLEMENT_OPTIONS,
        complementOption.toJson(),
        true
      );
      return UtilHelper.getIdFromLocationPath(response.location);
    } else {
      //UPDATE
      return await this.requestService.putData(
        APP_CONFIG.WEBSERVICE.COMPLEMENT_OPTIONS,
        complementOption.toJson()
      );
    }
  }

  async deleteComplementOption(complementOptionId: number): Promise<void> {
    return await this.requestService.deleteData(
      APP_CONFIG.WEBSERVICE.COMPLEMENT_OPTIONS + '/' + complementOptionId
    );
  }

  /**
   * PRODUCT AVAILABILITY
   * =====================================
   */

  async getProductAvailability(productId: number): Promise<Array<BusinessHour>> {
    const urlRequest: string = this.requestService.buildUrlPathParams(
      { productId: productId },
      APP_CONFIG.WEBSERVICE.GET_PRODUCT_AVAILIBILITY
    );
    return await this.requestService.getData(urlRequest);
  }

  async saveProductAvailability(
    productId: number,
    establishmentId: number,
    isEstablishmentHours: boolean,
    productHours?: Array<BusinessHour>
  ): Promise<any> {
    const data: any = {};
    if (!isEstablishmentHours) {
      const productHoursJson: Array<any> = new Array();
      productHours.forEach((b: BusinessHour) => {
        if (b.selected) {
          productHoursJson.push(b.toJson());
        }
      });
      data.productHours = productHoursJson;
    }
    data.productId = productId;
    data.establishmentHours = isEstablishmentHours;
    data.establishmentId = establishmentId;

    console.log(JSON.stringify(data));
    return await this.requestService.postData(
      APP_CONFIG.WEBSERVICE.SAVE_PRODUCT_AVAILIBILITY,
      data
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/category/products/product.service.ts